import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { ProjectDeletionRequest } from './projectDeletionLogic';

/**
 * Der Weg zum Loeschen eines Projekts — die einzige Stelle, die mit
 * `project_deletion_requests` und den vier RPCs aus
 * `supabase/project_deletion_approvals.sql` (MIGRATIONS.md Nr. 49) redet.
 *
 * Die REGELN liegen daneben in `src/services/projectDeletionLogic.ts` und sind
 * ohne Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu haben
 * ist.
 *
 * ═══ WARUM ES HIER KEIN `delete` GIBT ══════════════════════════════════════
 * Weil es keines mehr geben DARF. Auf `public.projects` liegt seit Nr. 49 ein
 * RESTRICTIVE-Deckel mit `using (false)` und darunter ein BEFORE-DELETE-
 * Trigger. Ein `supabase.from('projects').delete()` aus dem Client entfernt
 * seither nichts, sondern faellt auf die Nase — und das ist beabsichtigt:
 * Projekte verschwinden ausschliesslich ueber den Antragsweg
 *
 *     request → decide (durch einen ZWEITEN Menschen) → execute
 *
 * Wer hier eine Abkuerzung einbaut, baut sie gegen die Datenbank und bekommt
 * sie nicht.
 *
 * ═══ REIHENFOLGE BEIM AUSFUEHREN ═══════════════════════════════════════════
 * `fuehreLoeschungAus` raeumt ZUERST die Dokumentenablage im Storage und ruft
 * DANN die RPC. Das ist dieselbe Reihenfolge wie im alten Direktweg und aus
 * demselben Grund: `on delete cascade` raeumt nur die ZEILEN in
 * `project_documents` ab, die Dateien im Eimer kennt der Fremdschluessel
 * nicht. Ein Fehlschlag beim Aufraeumen bricht das Loeschen bewusst NICHT ab
 * — das Projekt zu behalten, weil eine Datei klemmt, hilft niemandem; der
 * Rest wird stattdessen in der Rueckmeldung benannt.
 */

const TABLE = 'project_deletion_requests';

const SPALTEN = `
    id, project_id, project_name, project_number, project_owner_id,
    company_id, company_name,
    requested_by, requested_by_name, reason,
    approver_id, approver_name, status,
    decided_by, decided_by_name, decided_at, decision_note,
    executed_at, executed_by, created_at, expires_at
`;

/**
 * Antrag stellen. Gibt die Antrags-ID zurueck.
 *
 * Der Zweitpruefer wird NICHT mitgeschickt — die Datenbank lost ihn aus.
 * Stuende er hier im Aufruf, koennte der Antragsteller ihn waehlen, und das
 * Vier-Augen-Prinzip waere ein Vier-Augen-Anschein.
 */
export const beantrageProjektLoeschung = async (
    projectId: string,
    begruendung: string,
): Promise<string> => {
    const { data, error } = await supabase.rpc('request_project_deletion', {
        p_project_id: projectId,
        p_reason: begruendung,
    });
    if (error) throw new Error(describeError(error));
    return data as string;
};

/** Zustimmen oder ablehnen. Erlaubt ist das nur dem ausgelosten Pruefer. */
export const entscheideProjektLoeschung = async (
    requestId: string,
    freigeben: boolean,
    notiz?: string,
): Promise<ProjectDeletionRequest> => {
    const { data, error } = await supabase.rpc('decide_project_deletion', {
        p_request_id: requestId,
        p_approve: freigeben,
        p_note: notiz || null,
    });
    if (error) throw new Error(describeError(error));
    return data as ProjectDeletionRequest;
};

/** Eigenen Antrag zuruecknehmen — auch eine bereits erteilte Freigabe. */
export const nimmLoeschantragZurueck = async (requestId: string): Promise<ProjectDeletionRequest> => {
    const { data, error } = await supabase.rpc('cancel_project_deletion_request', {
        p_request_id: requestId,
    });
    if (error) throw new Error(describeError(error));
    return data as ProjectDeletionRequest;
};

/**
 * Die freigegebene Loeschung ausfuehren.
 *
 * `dokumenteAufraeumen` wird von aussen hereingereicht, damit dieser Dienst
 * nichts vom Dokumentenmodul wissen muss (und der Test ihn ohne Storage
 * fahren kann). Liefert den Resttext, den die Oberflaeche anhaengt — leer,
 * wenn alles glatt lief.
 */
export const fuehreLoeschungAus = async (
    requestId: string,
    dokumenteAufraeumen?: () => Promise<void>,
): Promise<{ request: ProjectDeletionRequest; hinweis: string }> => {
    let hinweis = '';
    if (dokumenteAufraeumen) {
        try {
            await dokumenteAufraeumen();
        } catch {
            hinweis = ' Hinweis: Die Dateien der Dokumentenablage konnten nicht alle entfernt werden.';
        }
    }

    const { data, error } = await supabase.rpc('execute_project_deletion', {
        p_request_id: requestId,
    });
    if (error) throw new Error(describeError(error));
    return { request: data as ProjectDeletionRequest, hinweis };
};

/**
 * Abgelaufene Antraege in der Datenbank schliessen (MIGRATIONS.md Nr. 159).
 * Fail-open: fehlt die Nummer noch (`PGRST202`) oder klemmt der Aufruf, wird
 * eben nichts geschlossen — die Anzeige blendet Abgelaufenes ohnehin aus
 * (`istNochOffen`), und `request_project_deletion` raeumt selbst auf.
 */
export const schliesseAbgelaufeneAntraege = async (): Promise<number> => {
    try {
        const { data, error } = await supabase.rpc('close_expired_project_deletion_requests', {
            p_project_id: null,
        });
        if (error) return 0;
        return typeof data === 'number' ? data : 0;
    } catch {
        return 0;
    }
};

/**
 * Die Antraege, die dieser Nutzer sehen darf. Die Auswahl trifft die
 * SELECT-Policy — hier wird bewusst nicht noch einmal gefiltert, sonst
 * staenden zwei Regeln fuer dieselbe Frage.
 */
export const ladeLoeschantraege = async (nurOffene = false): Promise<ProjectDeletionRequest[]> => {
    await schliesseAbgelaufeneAntraege();
    let query = supabase.from(TABLE).select(SPALTEN).order('created_at', { ascending: false });
    if (nurOffene) {
        query = query.in('status', ['pending', 'approved']).gt('expires_at', new Date().toISOString());
    }

    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []) as unknown as ProjectDeletionRequest[];
};

/** Der offene Antrag zu EINEM Projekt, falls es einen gibt. */
export const ladeOffenenAntragZumProjekt = async (
    projectId: string,
): Promise<ProjectDeletionRequest | null> => {
    const { data, error } = await supabase
        .from(TABLE)
        .select(SPALTEN)
        .eq('project_id', projectId)
        .in('status', ['pending', 'approved'])
        .gt('expires_at', new Date().toISOString())
        .limit(1);
    if (error) throw new Error(describeError(error));
    return ((data || [])[0] as unknown as ProjectDeletionRequest) || null;
};

/**
 * Die offenen Antraege, die auf die Entscheidung DIESES Nutzers warten — fuer
 * das Pflicht-Popup. Kleine Abfrage ueber den Index
 * `project_deletion_requests_approver_idx`, deshalb darf sie oft laufen. Ob
 * der Nutzer wirklich entscheiden darf, beantwortet danach
 * `projectDeletionLogic.meinePruefungen`.
 */
export const ladeMeineOffenenPruefungen = async (userId: string): Promise<ProjectDeletionRequest[]> => {
    const { data, error } = await supabase
        .from(TABLE)
        .select(SPALTEN)
        .eq('approver_id', userId)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []) as unknown as ProjectDeletionRequest[];
};

/**
 * Signal zwischen Popup und Verwaltungsreiter: wer einen Antrag entschieden
 * hat, feuert es, die jeweils andere Ansicht laedt neu.
 */
export const LOESCHANTRAEGE_GEAENDERT = 'loeschantraege-geaendert';

export const meldeLoeschantraegeGeaendert = (): void => {
    try { window.dispatchEvent(new Event(LOESCHANTRAEGE_GEAENDERT)); } catch { /* ohne Fenster nichts zu melden */ }
};
